import { usePresentationTheme } from '@euler/app/tv/theme';
import { BlurView } from '@euler/components/BlurView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useNavigation } from '@react-navigation/native';
import { AnimatePresence, MotiView } from 'moti';
import {
  forwardRef,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { Subject } from 'rxjs';

const kExitAnimationDuration = 200;

export type FloatingModalScreenRef = {
  dismiss: (callback?: () => void) => void;
};

type Props = {
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  onDismissed$?: Subject<void>;
};

export const FloatingModalScreen = memo(
  forwardRef<FloatingModalScreenRef, Props>(
    ({ style, contentContainerStyle, children, onDismissed$ }, ref) => {
      const { height } = useWindowDimensions();
      const { rpx } = useResponsiveContext();
      const navigation = useNavigation();
      const theme = usePresentationTheme();
      const action = useRef<any>();
      const [dismissed, setDismissed] = useState(false);
      const dismissCallback = useRef<() => void>();

      useImperativeHandle(ref, () => ({
        dismiss: (callback?: () => void) => {
          dismissCallback.current = callback;
          navigation.goBack();
        },
      }));

      useEffect(() => {
        return navigation.addListener('beforeRemove', e => {
          action.current = e.data.action;
          e.preventDefault();
          setDismissed(true);
        });
      }, [navigation]);

      const onExit = useCallback(() => {
        if (action.current) {
          const removeListener = navigation.addListener('blur', () => {
            removeListener();
            if (onDismissed$) {
              onDismissed$.next();
            }
            dismissCallback.current?.();
          });
          navigation.dispatch(action.current);
        }
      }, [navigation, onDismissed$]);

      return (
        <AnimatePresence onExitComplete={onExit}>
          {!dismissed && (
            <MotiView
              from={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              exitTransition={{
                type: 'timing',
                duration: kExitAnimationDuration,
              }}
              css={`
                flex: 1;
                justify-content: center;
                align-items: center;
              `}
              style={style}
            >
              <BlurView
                blurType="dark"
                blurAmount={5}
                style={StyleSheet.absoluteFill}
                reducedTransparencyFallbackColor="white"
              />

              <TouchableWithoutFeedback
                style={StyleSheet.absoluteFill}
                onPress={() => navigation.goBack()}
              >
                <View style={StyleSheet.absoluteFill} />
              </TouchableWithoutFeedback>
              <MotiView
                from={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, translateY: height }}
                exitTransition={{
                  type: 'timing',
                  duration: kExitAnimationDuration,
                }}
                css={`
                  background-color: ${theme.modal.bgColor};
                  border-width: 0px;
                  border-color: ${theme.modal.borderColor};
                  width: ${rpx(950)}px;
                  border-radius: ${rpx(20)}px;
                  padding: ${rpx(56)}px;
                `}
                style={contentContainerStyle}
              >
                {children}
              </MotiView>
            </MotiView>
          )}
        </AnimatePresence>
      );
    },
  ),
);
